import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SharedHeader } from "@/components/SharedHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SocialShareButtons from "@/components/SocialShareButtons";
import { ArrowLeft, ArrowRight, Check, Phone, Calendar, CreditCard, Users, ShoppingBag, Building2, Stethoscope, Car, Home, Scale, Utensils, Quote, TrendingUp, Clock, DollarSign, Target } from "lucide-react";

// Import case study images
import healthcareImg from "@/assets/case-studies/healthcare-clinic.jpg";
import ecommerceImg from "@/assets/case-studies/ecommerce-shopify.jpg";
import hotelImg from "@/assets/case-studies/hotel-hospitality.jpg";
import realEstateImg from "@/assets/case-studies/real-estate.jpg";
import insuranceImg from "@/assets/case-studies/insurance-finance.jpg";
import restaurantImg from "@/assets/case-studies/restaurant.jpg";
import lawFirmImg from "@/assets/case-studies/law-firm.jpg";
import saasTechImg from "@/assets/case-studies/saas-tech.jpg";
import autoDealershipImg from "@/assets/case-studies/auto-dealership.jpg";
import homeServicesImg from "@/assets/case-studies/home-services.jpg";

// Static data that doesn't need translation
const caseStudyMeta: Record<string, { 
  heroImage: string; 
  icon: React.ReactNode; 
  callType: "inbound" | "outbound" | "both";
  integrations: string[];
}> = {
  "healthcare-clinic": {
    heroImage: healthcareImg,
    icon: <Stethoscope className="h-6 w-6" />,
    callType: "inbound",
    integrations: ["Google Calendar", "Epic EHR", "HIPAA Compliant", "SMS Gateway"],
  },
  "ecommerce-shopify": {
    heroImage: ecommerceImg,
    icon: <ShoppingBag className="h-6 w-6" />,
    callType: "outbound",
    integrations: ["Shopify", "Stripe", "Klaviyo", "HubSpot CRM"],
  },
  "hotel-hospitality": {
    heroImage: hotelImg,
    icon: <Building2 className="h-6 w-6" />,
    callType: "inbound",
    integrations: ["Opera PMS", "OpenTable", "Stripe", "Salesforce"],
  },
  "real-estate": {
    heroImage: realEstateImg,
    icon: <Home className="h-6 w-6" />,
    callType: "outbound",
    integrations: ["Salesforce", "Calendly", "Zillow", "Follow Up Boss"],
  },
  "insurance-finance": {
    heroImage: insuranceImg,
    icon: <CreditCard className="h-6 w-6" />,
    callType: "outbound",
    integrations: ["Stripe", "Salesforce", "Applied Epic", "DocuSign"],
  },
  "restaurant-group": {
    heroImage: restaurantImg,
    icon: <Utensils className="h-6 w-6" />,
    callType: "inbound",
    integrations: ["OpenTable", "Toast POS", "Square", "DoorDash"],
  },
  "law-firm": {
    heroImage: lawFirmImg,
    icon: <Scale className="h-6 w-6" />,
    callType: "both",
    integrations: ["Clio", "Google Calendar", "DocuSign", "Stripe"],
  },
  "saas-tech": {
    heroImage: saasTechImg,
    icon: <Users className="h-6 w-6" />,
    callType: "inbound",
    integrations: ["Zendesk", "Salesforce", "Slack", "Intercom"],
  },
  "auto-dealership": {
    heroImage: autoDealershipImg,
    icon: <Car className="h-6 w-6" />,
    callType: "outbound",
    integrations: ["VinSolutions CRM", "Google Calendar", "DealerSocket", "CDK"],
  },
  "home-services": {
    heroImage: homeServicesImg,
    icon: <Phone className="h-6 w-6" />,
    callType: "inbound",
    integrations: ["ServiceTitan", "Google Calendar", "Dispatch Board", "Stripe"],
  },
};

const validIds = [
  "healthcare-clinic",
  "ecommerce-shopify",
  "hotel-hospitality",
  "real-estate",
  "insurance-finance",
  "restaurant-group",
  "law-firm",
  "saas-tech",
  "auto-dealership",
  "home-services",
];

export default function CaseStudyDetail() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const currentLang = i18n.language;

  if (!id || !validIds.includes(id)) {
    return <Navigate to="/case-studies" replace />;
  }

  const meta = caseStudyMeta[id];
  const currentIndex = validIds.indexOf(id);
  const prevStudy = currentIndex > 0 ? validIds[currentIndex - 1] : null;
  const nextStudy = currentIndex < validIds.length - 1 ? validIds[currentIndex + 1] : null;

  // Get translated content
  const study = {
    title: t(`caseStudies.studies.${id}.title`),
    company: t(`caseStudies.studies.${id}.company`),
    industry: t(`caseStudies.studies.${id}.industry`),
    location: t(`caseStudies.studies.${id}.location`),
    employees: t(`caseStudies.studies.${id}.employees`),
    summary: t(`caseStudies.studies.${id}.summary`),
    challenge: t(`caseStudies.studies.${id}.challenge`),
    solution: t(`caseStudies.studies.${id}.solution`, { returnObjects: true }) as string[],
    implementation: t(`caseStudies.studies.${id}.implementation`),
    timeline: t(`caseStudies.studies.${id}.timeline`),
    testimonial: {
      quote: t(`caseStudies.studies.${id}.testimonial.quote`),
      author: t(`caseStudies.studies.${id}.testimonial.author`),
      role: t(`caseStudies.studies.${id}.testimonial.role`),
    },
    results: t(`caseStudies.studies.${id}.results`, { returnObjects: true }) as { metric: string; value: string; description: string }[],
    keyFeatures: t(`caseStudies.studies.${id}.keyFeatures`, { returnObjects: true }) as string[],
  };

  // Generate localized URL
  const getCaseStudyUrl = (caseId: string) => {
    if (currentLang && currentLang !== 'en') {
      return `/${currentLang}/case-studies/${caseId}`;
    }
    return `/case-studies/${caseId}`;
  };

  // Get call type label
  const getCallTypeLabel = () => {
    if (meta.callType === "both") return t("caseStudies.both");
    if (meta.callType === "inbound") return t("caseStudies.inbound");
    return t("caseStudies.outbound");
  };

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <SharedHeader showNav={false} />
      
      {/* Hero Section */}
      <section className="pt-28 pb-8 px-4">
        <div className="container mx-auto max-w-6xl">
          <Link to="/case-studies" className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("caseStudies.backToList")}
          </Link>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              {meta.icon}
              <span className="ml-1.5">{study.industry}</span>
            </Badge>
            <Badge variant={meta.callType === "inbound" ? "default" : meta.callType === "outbound" ? "secondary" : "outline"}>
              {getCallTypeLabel()}
            </Badge>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            {study.title}
          </h1>
          
          <div className="flex flex-wrap gap-6 text-muted-foreground mb-6">
            <span><strong className="text-foreground">{t("caseStudies.company")}:</strong> {study.company}</span>
            <span><strong className="text-foreground">{t("caseStudies.location")}:</strong> {study.location}</span>
            <span><strong className="text-foreground">{t("caseStudies.size")}:</strong> {study.employees}</span>
          </div>
          
          <SocialShareButtons />
        </div>
      </section>

      {/* Hero Image */}
      <section className="px-4 pb-12">
        <div className="container mx-auto max-w-6xl">
          <div className="rounded-2xl overflow-hidden border border-border">
            <img 
              src={meta.heroImage} 
              alt={study.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Results Banner */}
      <section className="px-4 pb-12">
        <div className="container mx-auto max-w-6xl">
          <Card className="bg-gradient-to-br from-primary/10 via-background to-primary/5 border-primary/20">
            <CardContent className="p-8">
              <h2 className="text-xl font-bold mb-6 text-center">{t("caseStudies.keyResults")}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {Array.isArray(study.results) && study.results.map((result, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-primary mb-1">{result.value}</div>
                    <div className="text-sm font-medium text-foreground mb-1">{result.metric}</div>
                    <div className="text-xs text-muted-foreground">{result.description}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-4 pb-12">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Summary */}
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Target className="h-6 w-6 text-primary" />
                  {t("caseStudies.overview")}
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {study.summary}
                </p>
              </div>

              {/* Challenge */}
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="h-6 w-6 text-primary" />
                  {t("caseStudies.challenge")}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {study.challenge}
                </p>
              </div>

              {/* Solution */}
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Check className="h-6 w-6 text-primary" />
                  {t("caseStudies.solution")}
                </h2>
                <ul className="space-y-3">
                  {Array.isArray(study.solution) && study.solution.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="mt-1 h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Check className="h-3 w-3 text-primary" />
                      </div>
                      <span className="text-muted-foreground">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Implementation */}
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Clock className="h-6 w-6 text-primary" />
                  {t("caseStudies.implementation")}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {study.implementation}
                </p>
                <Badge variant="outline" className="text-sm">
                  {t("caseStudies.timeline")}: {study.timeline}
                </Badge>
              </div>

              {/* Testimonial */}
              <Card className="bg-muted/50 border-border">
                <CardContent className="p-8">
                  <Quote className="h-10 w-10 text-primary/30 mb-4" />
                  <blockquote className="text-lg italic text-foreground mb-6 leading-relaxed">
                    "{study.testimonial.quote}"
                  </blockquote>
                  <div>
                    <div className="font-semibold text-foreground">{study.testimonial.author}</div>
                    <div className="text-sm text-muted-foreground">{study.testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Key Features */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold mb-4">{t("caseStudies.keyFeatures")}</h3>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(study.keyFeatures) && study.keyFeatures.map((feature, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Integrations */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold mb-4">{t("caseStudies.integrations")}</h3>
                  <div className="flex flex-wrap gap-2">
                    {meta.integrations.map((integration, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {integration}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* CTA */}
              <Card className="bg-primary text-primary-foreground">
                <CardContent className="p-6 text-center">
                  <DollarSign className="h-10 w-10 mx-auto mb-4 opacity-80" />
                  <h3 className="font-bold text-lg mb-2">{t("caseStudies.getSimilarResults")}</h3>
                  <p className="text-sm opacity-90 mb-4">
                    {t("caseStudies.seeHow")} {study.industry.toLowerCase()} {t("caseStudies.business")}.
                  </p>
                  <Link to="/chat-to-agent">
                    <Button variant="secondary" className="w-full">
                      {t("caseStudies.buildYourAgent")}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="px-4 pb-12">
        <div className="container mx-auto max-w-6xl">
          <div className="flex justify-between items-center border-t border-border pt-8">
            {prevStudy ? (
              <Link to={getCaseStudyUrl(prevStudy)} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{t("caseStudies.previous")}: {t(`caseStudies.studies.${prevStudy}.company`)}</span>
                <span className="sm:hidden">{t("caseStudies.previous")}</span>
              </Link>
            ) : <div />}
            
            <Link to="/case-studies" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              {t("caseStudies.viewAll")}
            </Link>
            
            {nextStudy ? (
              <Link to={getCaseStudyUrl(nextStudy)} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                <span className="hidden sm:inline">{t("caseStudies.next")}: {t(`caseStudies.studies.${nextStudy}.company`)}</span>
                <span className="sm:hidden">{t("caseStudies.next")}</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-4">
        <div className="container mx-auto max-w-6xl flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © 2025 Call24x7. {t("footer.rights")}
          </p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t("footer.privacy")}
            </Link>
            <Link to="/terms-of-use" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t("footer.terms")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}